import React, { useState, useRef, useEffect } from 'react';
import { colors, borderRadius, fontSize, fontWeight, shadows, transitions } from '../../constants/theme';

interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface SelectProps {
  options: SelectOption[];
  value?: string | number;
  onChange?: (value: string | number) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  searchable?: boolean;
  multiple?: boolean;
  clearable?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

const Select: React.FC<SelectProps> = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select an option',
  label,
  error,
  helperText,
  fullWidth = false,
  disabled = false,
  searchable = false,
  multiple = false,
  clearable = false,
  style,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedValues, setSelectedValues] = useState<(string | number)[]>(
    multiple ? (Array.isArray(value) ? value : []) : value ? [value] : []
  );
  const selectRef = useRef<HTMLDivElement>(null);
  const hasError = !!error;

  const filteredOptions = searchable
    ? (options || []).filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : (options || []);

  const getDisplayValue = (): string => {
    if (!options || options.length === 0) return '';
    
    if (multiple) {
      const selected = options.filter(opt => selectedValues.includes(opt.value));
      return selected.length > 0 
        ? selected.map(opt => opt.label).join(', ')
        : '';
    } else {
      const selected = options.find(opt => opt.value === value);
      return selected ? selected.label : '';
    }
  };

  const handleSelect = (optionValue: string | number) => {
    if (multiple) {
      const newValues = selectedValues.includes(optionValue)
        ? selectedValues.filter(v => v !== optionValue)
        : [...selectedValues, optionValue];
      setSelectedValues(newValues);
      if (onChange) onChange(newValues as any);
    } else {
      if (onChange) onChange(optionValue);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (multiple) {
      setSelectedValues([]);
      if (onChange) onChange([] as any);
    } else {
      if (onChange) onChange('');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const containerStyles: React.CSSProperties = {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: '6px',
    width: fullWidth ? '100%' : 'auto',
    minWidth: '250px',
    position: 'relative'
  };

  const labelStyles: React.CSSProperties = {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    color: hasError ? colors.ui.error : colors.text.primary
  };

  const selectButtonStyles: React.CSSProperties = {
    width: '100%',
    padding: '10px 36px 10px 12px',
    fontSize: fontSize.base,
    color: getDisplayValue() ? colors.text.primary : colors.text.secondary,
    backgroundColor: disabled ? colors.ui.bgSecondary : colors.ui.bg,
    border: `1.5px solid ${
      hasError 
        ? colors.ui.error 
        : isOpen 
          ? colors.accent.blue 
          : colors.ui.border
    }`,
    borderRadius: borderRadius.md,
    cursor: disabled ? 'not-allowed' : 'pointer',
    textAlign: 'left',
    transition: `all ${transitions.fast}`,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...(isOpen && {
      boxShadow: hasError
        ? `0 0 0 3px rgba(232, 92, 86, 0.1)`
        : `0 0 0 3px rgba(77, 143, 234, 0.1)`
    }),
    ...style
  };

  const dropdownStyles: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    backgroundColor: colors.ui.bg,
    border: `1px solid ${colors.ui.border}`,
    borderRadius: borderRadius.md,
    boxShadow: shadows.lg,
    maxHeight: '240px',
    overflowY: 'auto',
    zIndex: 1000,
    opacity: isOpen ? 1 : 0,
    visibility: isOpen ? 'visible' : 'hidden',
    transform: isOpen ? 'translateY(0)' : 'translateY(-10px)',
    transition: `all ${transitions.fast}`
  };

  const optionStyles = (isSelected: boolean, isDisabled?: boolean): React.CSSProperties => ({
    padding: '10px 12px',
    fontSize: fontSize.base,
    color: isDisabled ? colors.text.secondary : colors.text.primary,
    backgroundColor: isSelected ? colors.ui.bgSecondary : 'transparent',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: `background-color ${transitions.fast}`,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    opacity: isDisabled ? 0.5 : 1
  });

  const chevronStyles: React.CSSProperties = {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: `translateY(-50%) rotate(${isOpen ? '180deg' : '0deg'})`,
    transition: `transform ${transitions.fast}`,
    pointerEvents: 'none',
    color: colors.text.secondary
  };

  const searchInputStyles: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    fontSize: fontSize.sm,
    border: 'none',
    borderBottom: `1px solid ${colors.ui.border}`,
    outline: 'none',
    backgroundColor: 'transparent'
  };

  const messageStyles: React.CSSProperties = {
    fontSize: fontSize.xs,
    color: hasError ? colors.ui.error : colors.text.secondary,
    marginTop: '2px'
  };

  return (
    <div style={containerStyles} ref={selectRef}>
      {label && <label style={labelStyles}>{label}</label>}
      
      <div style={{ position: 'relative', width: '100%' }}>
        <div
          style={selectButtonStyles}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-disabled={disabled}
        >
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {getDisplayValue() || placeholder}
          </span>
          {clearable && getDisplayValue() && !disabled && (
            <span
              onClick={handleClear}
              style={{
                marginRight: '20px',
                cursor: 'pointer',
                color: colors.text.secondary,
                fontSize: '16px'
              }}
            >
              ×
            </span>
          )}
          <span style={chevronStyles}>▼</span>
        </div>

        <div style={dropdownStyles} role="listbox">
          {searchable && isOpen && (
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              style={searchInputStyles}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => {
              const isSelected = multiple 
                ? selectedValues.includes(option.value)
                : option.value === value;
              
              return (
                <div
                  key={option.value}
                  style={optionStyles(isSelected, option.disabled)}
                  onClick={() => !option.disabled && handleSelect(option.value)}
                  onMouseEnter={(e) => {
                    if (!option.disabled) {
                      e.currentTarget.style.backgroundColor = colors.ui.bgSecondary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!option.disabled && !isSelected) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={option.disabled}
                >
                  {multiple && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      style={{ margin: 0 }}
                    />
                  )}
                  {option.label}
                </div>
              );
            })
          ) : (
            <div style={{ padding: '10px 12px', color: colors.text.secondary, textAlign: 'center' }}>
              No options found
            </div>
          )}
        </div>
      </div>

      {(error || helperText) && (
        <span style={messageStyles}>
          {error || helperText}
        </span>
      )}
    </div>
  );
};

export default Select;
export { Select };